import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMotionValue } from 'framer-motion';

// Master PechaKucha clock. One rAF loop; exposes scene index + local time (10Hz
// React state for choreography gates) and motion values (60fps for continuous motion).
export function useClock({ scenes, duration, initialIndex = 0, autoplay = false, autoAdvance = true, speed = 1 }) {
  const [index, setIndex] = useState(Math.min(Math.max(initialIndex, 0), scenes.length - 1));
  const [playing, setPlaying] = useState(autoplay);
  const [t, setT] = useState(0);
  const tMV = useMotionValue(0);
  const groupTMV = useMotionValue(0);

  const elapsed = useRef(0);
  const indexRef = useRef(index);
  const playingRef = useRef(playing);
  const advanceRef = useRef(autoAdvance);
  const speedRef = useRef(speed);
  const last = useRef(null);
  playingRef.current = playing;
  advanceRef.current = autoAdvance;
  speedRef.current = speed;

  const groupStart = useMemo(() => {
    const g = scenes[index]?.group;
    if (!g) return index;
    let s = index;
    while (s > 0 && scenes[s - 1].group === g) s -= 1;
    return s;
  }, [scenes, index]);

  const groupStartRef = useRef(groupStart);
  groupStartRef.current = groupStart;

  const goTo = useCallback((i, { keepPlaying = true } = {}) => {
    const clamped = Math.min(Math.max(i, 0), scenes.length - 1);
    indexRef.current = clamped;
    elapsed.current = 0;
    last.current = null;
    setIndex(clamped);
    setT(0);
    tMV.set(0);
    if (!keepPlaying) setPlaying(false);
  }, [scenes.length, tMV]);

  useEffect(() => {
    let raf;
    const tick = (now) => {
      if (last.current == null) last.current = now;
      // Real elapsed time so slides stay exactly 20s even when rendering drops frames; a gap > 1s (hidden tab / stall) is treated as a pause.
      const raw = now - last.current;
      const dt = (raw > 1000 ? 0 : raw) * speedRef.current;
      last.current = now;
      if (playingRef.current) {
        elapsed.current += dt;
        if (elapsed.current >= duration * 1000) {
          if (indexRef.current < scenes.length - 1 && advanceRef.current) {
            indexRef.current += 1;
            elapsed.current -= duration * 1000; // carry the overshoot so 20 slides = exactly 20 × duration
            setIndex(indexRef.current);
          } else {
            elapsed.current = duration * 1000;
            setPlaying(false);
          }
        }
      }
      const sec = elapsed.current / 1000;
      if (typeof window !== 'undefined') window.__pkClock = { index: indexRef.current, t: sec, playing: playingRef.current };
      tMV.set(sec);
      const stage = indexRef.current - groupStartRef.current;
      groupTMV.set(stage * duration + sec);
      const coarse = Math.floor(sec * 10) / 10;
      setT((prev) => (prev === coarse ? prev : coarse));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [duration, scenes.length]);

  const next = useCallback(() => goTo(indexRef.current + 1), [goTo]);
  const prev = useCallback(() => goTo(indexRef.current - 1), [goTo]);
  const restart = useCallback(() => { goTo(0); setPlaying(true); }, [goTo]);
  const replayScene = useCallback(() => { goTo(indexRef.current); setPlaying(true); }, [goTo]);
  const toggle = useCallback(() => {
    setPlaying((p) => {
      if (!p && elapsed.current >= duration * 1000 && indexRef.current === scenes.length - 1) return p;
      if (!p && elapsed.current >= duration * 1000) { indexRef.current += 1; elapsed.current = 0; setIndex(indexRef.current); setT(0); }
      return !p;
    });
  }, [duration, scenes.length]);
  const play = useCallback(() => setPlaying(true), []);
  const pause = useCallback(() => setPlaying(false), []);

  const stage = index - groupStart;
  const overall = index * duration + t;
  const finished = index === scenes.length - 1 && t >= duration;

  return { index, t, playing, stage, groupT: stage * duration + t, overall, finished, tMV, groupTMV, goTo, next, prev, restart, replayScene, toggle, play, pause, setPlaying };
}
